import { Dispatch, SetStateAction, useState, useEffect } from "react";
import {
  validEmail,
  validPassword,
  validStrongPassword,
  validStrongPasswordForCA,
  validName,
} from "@Components/forms/helpers/FieldValidator";
import { FORM_FIELD_KEYS, LOCALE } from "@Constants/common";
import { findErrorCode } from "@Utils/errors";
import PATTERNS from "@Utils/patterns";
import { IHeadingAndMessage } from "@Utils/snackbarHandlers";
import { areAllKeysPresent, isKeyPresent } from "@Utils/validation";
import {
  ERROR_CODES,
  IErrorCodeTranslation,
  TErrorCode,
} from "@wff/api/constants/error";
import { ISignup } from "@wff/interfaces";

export const FORM_TYPES = {
  SIGNUP: "signup",
  LOGIN: "login",
  FORGOT_PASSWORD: "forgot_password",
  ACCOUNT_RESET: "account_reset",
};

export interface IValidationTranslation {
  allFieldsBlank?: string;
  firstNameBlank?: string;
  lastNameBlank?: string;
  emailBlank?: string;
  emailNeedsContentAfterAtSymbol?: string;
  passwordBlank?: string;
  confirmPasswordBlank?: string;
  passwordsDoNotMatch?: string;
}

export interface IGetValidationErrorByLocale {
  type: string;
  error?: any;
  touched: boolean;
  formState: ISignup;
  languageCode: string;
  errorCodeTranslation?: IErrorCodeTranslation;
  validationTranslation?: Partial<IValidationTranslation>;
}

export interface IUseFormFieldValidation {
  type: string;
  touched?: boolean; // optional for assigning the default value
  formState: any;
  formAction: () => void;
  languageCode: string;
  errorCodeTranslation?: IErrorCodeTranslation;
  validationTranslation?: Partial<IValidationTranslation>;
}

export interface IUseFormFieldValidationResult {
  touched: boolean;
  setTouched: Dispatch<SetStateAction<boolean>>;
  formError: any;
  setFormError: Dispatch<SetStateAction<any>>;
  formErrorMessages: string[];
  setFormErrorMessages: Dispatch<SetStateAction<string[]>>;
  setFormActionIsActive: Dispatch<SetStateAction<boolean>>;
}

export interface IResetFormActionActiveStatus {
  setFormError: Dispatch<SetStateAction<any>>;
  setFormActionIsActive: Dispatch<SetStateAction<boolean>>;
}

export interface IResetFormFieldErrorMessage {
  setFormErrorMessages: Dispatch<SetStateAction<string[]>>;
}

export interface IGetFormErrorCodeMessage {
  error: any;
  errorCodeTranslation: IErrorCodeTranslation;
}

export interface IGetFormValidationMessage {
  type: string;
  formState: ISignup;
  languageCode: string;
  validationTranslation: Partial<IValidationTranslation>;
}

export const getFormErrorCodeMessage = ({
  error,
  errorCodeTranslation,
}: IGetFormErrorCodeMessage) => {
  const formErrMsgs: string[] = [];
  Object.keys(errorCodeTranslation).forEach((key) => {
    const matchingError = findErrorCode({
      code: ERROR_CODES[key as TErrorCode],
      error,
    });
    if (matchingError) {
      if (typeof errorCodeTranslation[key as TErrorCode] === "string") {
        formErrMsgs.push(
          (errorCodeTranslation[key as TErrorCode] as string) ?? ""
        );
      } else {
        formErrMsgs.push(
          (errorCodeTranslation[key as TErrorCode] as IHeadingAndMessage)
            ?.message ?? ""
        );
      }
    }
  });

  return formErrMsgs;
};

export const getFormValidationMessage = ({
  type,
  formState,
  languageCode,
  validationTranslation,
}: // eslint-disable-next-line sonarjs/cognitive-complexity
IGetFormValidationMessage) => {
  const formErrMsgs: string[] = [];
  switch (languageCode) {
    case LOCALE.EN_US:
    case LOCALE.EN_CA:
    case LOCALE.FR_CA:
    case LOCALE.ES_MX:
    default:
      if (
        type === FORM_TYPES.SIGNUP &&
        areAllKeysPresent(
          [
            FORM_FIELD_KEYS.EMAIL,
            FORM_FIELD_KEYS.PASSWORD,
            FORM_FIELD_KEYS.FIRST_NAME,
            FORM_FIELD_KEYS.FIRST_NAME,
          ],
          formState
        ) &&
        !formState?.email &&
        !formState?.password &&
        !formState?.firstName &&
        !formState?.lastName
      ) {
        formErrMsgs.push(validationTranslation?.allFieldsBlank ?? "");
        return formErrMsgs;
      }
      if (
        areAllKeysPresent(
          [FORM_FIELD_KEYS.PASSWORD, FORM_FIELD_KEYS.CONFIRM_PASSWORD],
          formState
        ) &&
        formState?.password !== formState?.confirmedPassword
      ) {
        formErrMsgs.push(validationTranslation?.passwordsDoNotMatch ?? "");
      }
      if (
        isKeyPresent(FORM_FIELD_KEYS.EMAIL, formState) &&
        PATTERNS.EMAIL_ENDS_WITH_AT.test(formState?.email)
      ) {
        formErrMsgs.push(
          validationTranslation?.emailNeedsContentAfterAtSymbol ?? ""
        );
      }
      if (
        isKeyPresent(FORM_FIELD_KEYS.EMAIL, formState) &&
        !validEmail(formState?.email)
      ) {
        formErrMsgs.push(validationTranslation?.emailBlank ?? "");
      }
      if (
        isKeyPresent(FORM_FIELD_KEYS.FIRST_NAME, formState) &&
        !validName(formState?.firstName)
      ) {
        formErrMsgs.push(validationTranslation?.firstNameBlank ?? "");
      }
      if (
        isKeyPresent(FORM_FIELD_KEYS.LAST_NAME, formState) &&
        !validName(formState?.lastName)
      ) {
        formErrMsgs.push(validationTranslation?.lastNameBlank ?? "");
      }
      if (
        type === FORM_TYPES.LOGIN &&
        isKeyPresent(FORM_FIELD_KEYS.PASSWORD, formState) &&
        !validPassword(formState?.password)
      ) {
        formErrMsgs.push(validationTranslation?.passwordBlank ?? "");
      }
      if (
        (languageCode === LOCALE.EN_US || languageCode === LOCALE.ES_MX) &&
        type !== FORM_TYPES.LOGIN &&
        isKeyPresent(FORM_FIELD_KEYS.PASSWORD, formState) &&
        !validStrongPassword(formState?.password)
      ) {
        formErrMsgs.push(validationTranslation?.passwordBlank ?? "");
      }
      if (
        (languageCode === LOCALE.EN_CA || languageCode === LOCALE.FR_CA) &&
        type !== FORM_TYPES.LOGIN &&
        isKeyPresent(FORM_FIELD_KEYS.PASSWORD, formState) &&
        !validStrongPasswordForCA(formState?.password)
      ) {
        formErrMsgs.push(validationTranslation?.passwordBlank ?? "");
      }
  }

  return formErrMsgs;
};

export const getFormValidationErrorByLocale = ({
  type,
  error,
  touched,
  formState,
  languageCode,
  errorCodeTranslation,
  validationTranslation,
}: IGetValidationErrorByLocale) => {
  let formErrMsgs: string[] = [];
  // NOTE: remove this note when it is determined that a locale logic-fork is no longer relevant: at the time this was coded, there was no logical difference between the locales
  switch (languageCode) {
    case LOCALE.EN_US:
    case LOCALE.EN_CA:
    case LOCALE.FR_CA:
    case LOCALE.ES_MX:
    default:
      if (touched) {
        formErrMsgs = [
          ...formErrMsgs,
          ...getFormErrorCodeMessage({
            // api error response validation
            error,
            errorCodeTranslation: errorCodeTranslation!,
          }),
          ...getFormValidationMessage({
            // standard input validation
            type,
            formState,
            languageCode,
            validationTranslation: validationTranslation!,
          }),
        ];
      }
      break;
  }
  return formErrMsgs;
};

export const resetFormActionActiveStatus = ({
  setFormError,
  setFormActionIsActive,
}: IResetFormActionActiveStatus) => {
  setFormError(null);
  setFormActionIsActive(false);
};

export const resetFormFieldErrorMessage = ({
  setFormErrorMessages,
}: IResetFormFieldErrorMessage) => {
  setFormErrorMessages([]);
};

export const useFormFieldValidation = ({
  type,
  touched: touchedDefault,
  formState,
  formAction,
  languageCode,
  errorCodeTranslation,
  validationTranslation,
}: IUseFormFieldValidation): IUseFormFieldValidationResult => {
  const [touched, setTouched] = useState(touchedDefault ?? false);
  const [formError, setFormError] = useState(null); // error response from api call
  const [formErrorMessages, setFormErrorMessages] = useState<string[]>([]); // form error messages to be rendered
  const [formActionIsActive, setFormActionIsActive] = useState(false);

  useEffect(() => {
    if (formActionIsActive || formError) {
      const formErrMsgs: string[] = getFormValidationErrorByLocale({
        type,
        error: formError,
        touched,
        formState,
        languageCode,
        errorCodeTranslation,
        validationTranslation,
      });
      setFormErrorMessages(formErrMsgs);
      resetFormActionActiveStatus({ setFormError, setFormActionIsActive }); // reset the state params that were responsible for triggering this useEffect
      if (formActionIsActive && !formErrMsgs?.length) {
        resetFormFieldErrorMessage({ setFormErrorMessages }); // reset all error message state before submitting form
        formAction();
      }
    }
  }, [
    type,
    touched,
    formActionIsActive,
    formError,
    errorCodeTranslation,
    validationTranslation,
    formAction,
    formState,
    languageCode,
  ]);

  return {
    touched,
    setTouched,
    formError,
    setFormError,
    formErrorMessages,
    setFormErrorMessages,
    setFormActionIsActive,
  };
};
